import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  Category as CategoryIcon,
  TrendingDown as TrendingDownIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";

import InventoryService from "../../api/inventory";

const CATEGORY_LABELS = {
  carnes: "Carnes",
  laticinios: "Laticinios",
  hortifruti: "Hortifuti",
  graos: "Graos e Cereais",
  bebidas: "Bebidas",
  temperos: "Temperos",
  embalagens: "Embalagens",
  limpeza: "Limpeza",
  outros: "Outros",
};

const StockDashboard = ({ showAlert }) => {
  const theme = useTheme();
  const [summary, setSummary] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, lowStockRes] = await Promise.all([
        InventoryService.getStockSummary(),
        InventoryService.getLowStockIngredients(),
      ]);
      setSummary(summaryRes.data);
      setLowStock(lowStockRes.data || []);
    } catch (err) {
      showAlert("Erro ao carregar dashboard", "error");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatMoney = (val) => {
    if (val === null || val === undefined) return "R$ 0,00";
    return Number(val).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatNumber = (val) =>
    Number(val || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Carregando dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
        Dashboard do Estoque
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha("#2563eb", 0.2) }}
          >
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <InventoryIcon sx={{ color: "#2563eb", fontSize: 28 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Ingredientes
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {summary?.total_ingredients || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor:
                summary?.low_stock_count > 0
                  ? alpha("#ef4444", 0.3)
                  : alpha("#22c55e", 0.2),
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <WarningIcon
                  sx={{
                    color: summary?.low_stock_count > 0 ? "#ef4444" : "#22c55e",
                    fontSize: 28,
                  }}
                />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Estoque Baixo
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={summary?.low_stock_count > 0 ? "error.main" : "success.main"}
                  >
                    {summary?.low_stock_count || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha("#8b5cf6", 0.2) }}
          >
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CategoryIcon sx={{ color: "#8b5cf6", fontSize: 28 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Categorias Ativas
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {summary?.by_category?.length || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha("#f59e0b", 0.2) }}
          >
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TrendingDownIcon sx={{ color: "#f59e0b", fontSize: 28 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Valor em Estoque
                  </Typography>
                  <Typography variant="h5" fontWeight={700} sx={{ fontSize: "1.2rem" }}>
                    {formatMoney(
                      summary?.by_category?.reduce(
                        (acc, cat) => acc + Number(cat.total_value || 0),
                        0
                      )
                    )}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* By Category */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Estoque por Categoria
            </Typography>
            {summary?.by_category?.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Itens</TableCell>
                    <TableCell align="right">Valor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.by_category.map((cat) => (
                    <TableRow key={cat.category}>
                      <TableCell>
                        <Chip
                          label={CATEGORY_LABELS[cat.category] || cat.category}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{cat.count}</TableCell>
                      <TableCell align="right">
                        {formatMoney(cat.total_value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                Nenhum dado disponivel.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Low Stock Alert */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <WarningIcon sx={{ color: "#ef4444", fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Alertas de Estoque Baixo
              </Typography>
            </Stack>
            {lowStock.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ingrediente</TableCell>
                    <TableCell align="right">Atual</TableCell>
                    <TableCell align="right">Minimo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lowStock.map((item) => (
                    <TableRow
                      key={item.id}
                      sx={{ backgroundColor: alpha("#ef4444", 0.04) }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="error.main" fontWeight={600}>
                          {formatNumber(item.current_quantity)} {item.unit}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(item.minimum_stock)} {item.unit}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography
                color="success.main"
                sx={{ py: 2, textAlign: "center", fontWeight: 500 }}
              >
                Todos os ingredientes estao com estoque adequado.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StockDashboard;
